/**
 * The suite products an org holds (migration 0227; plan of record docs/plans/2026-10-13_microvision-and-the-account-layer.md,
 * section A): Foundry Twin, this app, and Foundry Scope (key "microvision"), a product of its own on its own host.
 * /v1/platform/auth/me lists each org's products, Twin first, with the member's role in each.
 *
 * The app switcher offers another product only where the org holds it usable, the member has a role in it, and this
 * build knows its address (VITE_FOUNDRY_SCOPE_URL; unset, the entry stays hidden). A link to it names the org
 * (?org=<uuid>); the other product links back the same way, and the org provider takes it (orgParamOf, linkedOrgOf).
 */

export const TWIN = "twin";
export const FOUNDRY_SCOPE = "microvision";

/** A suite product as /auth/me lists it for one org. */
export interface SuiteProduct {
  key: string;
  name: string;
  status: "trial" | "active" | "suspended";
  /** The end of a trial or a term; null when open ended. */
  until: string | null;
  /** Active or a trial still running, and not past its end. */
  usable: boolean;
  /** The member's role in the product; Twin's is the membership role. Empty when they have none. */
  role: string;
}

/** The wire shape of one product in /auth/me. */
export interface SuiteProductWire {
  key: string;
  name: string;
  status: string;
  until?: string | null;
  usable?: boolean;
  role?: string;
}

const STATUSES = new Set(["trial", "active", "suspended"]);

/** Maps /auth/me's products; an older API that lists none gives an empty array. */
export function mapProducts(raw: SuiteProductWire[] | null | undefined): SuiteProduct[] {
  return (raw ?? []).map((p) => ({
    key: p.key,
    name: p.name,
    status: (STATUSES.has(p.status) ? p.status : "suspended") as SuiteProduct["status"],
    until: p.until ?? null,
    usable: !!p.usable,
    role: p.role ?? "",
  }));
}

/** Foundry Scope's roles, least first (org_member_suite_roles; internal/suite). */
export const FOUNDRY_SCOPE_ROLES = ["viewer", "metallographer", "approver", "admin"] as const;

export const FOUNDRY_SCOPE_ROLE_LABELS: Record<(typeof FOUNDRY_SCOPE_ROLES)[number], string> = {
  viewer: "Viewer",
  metallographer: "Metallographer",
  approver: "Approver",
  admin: "Admin",
};

/** The address of another product's web app in this build, or null when unset. */
export function productHome(key: string, env: { VITE_FOUNDRY_SCOPE_URL?: string } = import.meta.env): string | null {
  if (key !== FOUNDRY_SCOPE) return null;
  const base = (env.VITE_FOUNDRY_SCOPE_URL ?? "").trim().replace(/\/+$/, "");
  return /^https?:\/\//.test(base) ? base : null;
}

/** An entry of the app switcher: another product the member can open, with the link that opens it in this org. */
export interface SwitcherEntry {
  key: string;
  name: string;
  href: string;
  /** The end of a trial, for its note. */
  trialUntil: string | null;
}

/** The other products the member can open from this org: held usable, a role in it, and an address in this build. */
export function switcherEntries(
  orgId: string | null | undefined,
  products: SuiteProduct[] | undefined,
  env?: { VITE_FOUNDRY_SCOPE_URL?: string },
): SwitcherEntry[] {
  if (!orgId) return [];
  const out: SwitcherEntry[] = [];
  for (const p of products ?? []) {
    if (p.key === TWIN || !p.usable || !p.role) continue;
    const home = productHome(p.key, env);
    if (!home) continue;
    out.push({
      key: p.key,
      name: p.name,
      href: `${home}/?org=${encodeURIComponent(orgId)}`,
      trialUntil: p.status === "trial" ? p.until : null,
    });
  }
  return out;
}

/** The suite product of this key the org holds, if any. */
export function heldProduct(products: SuiteProduct[] | undefined, key: string): SuiteProduct | undefined {
  return (products ?? []).find((p) => p.key === key);
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** What an address's query says of a linked org: whether it names one at all, the org (null when malformed), and the
 *  query without it, which the org provider puts back through the router so no page copies org= back. */
export interface OrgParam {
  present: boolean;
  org: string | null;
  /** The query without org, "" or starting with "?". */
  rest: string;
}

export function orgParamOf(search: string): OrgParam {
  const params = new URLSearchParams(search);
  if (!params.has("org")) return { present: false, org: null, rest: search };
  const org = params.get("org") ?? "";
  params.delete("org");
  const rest = params.toString();
  return { present: true, org: UUID.test(org) ? org.toLowerCase() : null, rest: rest ? `?${rest}` : "" };
}

/**
 * What a linked org that waits for the user's orgs becomes: the active org once their orgs hold it ("promote"),
 * dropped once their orgs are known and do not ("drop"), or kept waiting while they are unknown ("wait"). A link to an
 * org the user is not in never replaces their stored choice (#1399 review).
 */
export function linkedOrgOf(pending: string | null, orgIds: string[]): "promote" | "drop" | "wait" | null {
  if (!pending) return null;
  if (orgIds.length === 0) return "wait";
  return orgIds.includes(pending) ? "promote" : "drop";
}
